import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useTranslation } from 'react-i18next';

type TimeInputProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
    value?: number; // Decimal hours (e.g., 1.5 for 1 hour 30 minutes)
    onChange?: (decimalHours: number | undefined) => void;
    onValidChange?: (isValid: boolean) => void;
};

/**
 * TimeInput component that allows users to input time in HH:MM format
 * while storing and processing data as decimal hours (floats).
 *
 * Examples:
 * - Input: "1:30" → Output: 1.5 hours
 * - Input: "1:15" → Output: 1.25 hours
 * - Input: "0:45" → Output: 0.75 hours
 * - Input: "2:00" → Output: 2 hours
 */
const TimeInput: FC<TimeInputProps> = ({ value, onChange, onValidChange, ...textFieldProps }) => {
    const { t: translate } = useTranslation();
    const [displayValue, setDisplayValue] = useState<string>('');
    const [isValid, setIsValid] = useState<boolean>(true);
    const isEditingRef = useRef<boolean>(false);
    const previousValueRef = useRef<number | undefined>(undefined);

    // Convert decimal hours to HH:MM format
    const decimalToTimeString = useCallback((decimalHours: number | undefined): string => {
        if (decimalHours === undefined || decimalHours === null) {
            return '';
        }

        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);

        // Handle edge case where rounding brings minutes to 60
        if (minutes === 60) {
            return `${hours + 1}:00`;
        }

        return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }, []);

    // Convert HH:MM format to decimal hours
    const timeStringToDecimal = useCallback((timeString: string): number | undefined => {
        if (!timeString || timeString.trim() === '') {
            return undefined;
        }

        // Match HH:MM format (hours can be any non-negative integer up to 5 digits)
        const match = timeString.trim().match(/^(\d{1,5}):(\d{2})$/);

        if (!match) {
            return undefined;
        }

        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);

        // Validate
        if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes > 59) {
            return undefined;
        }

        return hours + minutes / 60;
    }, []);

    // Validate time string format
    const validateTimeString = useCallback((timeString: string): string => {
        if (!timeString || timeString.trim() === '') {
            return '';
        }

        const trimmed = timeString.trim();

        // Allow bare digits — will be auto-formatted on blur
        if (/^\d{1,5}$/.test(trimmed)) {
            return '';
        }

        const match = trimmed.match(/^(\d{1,5}):(\d{2})$/);

        if (!match) {
            return translate('common:INVALID_FORMAT');
        }

        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);

        if (isNaN(hours) || isNaN(minutes) || hours < 0) {
            return 'Hours must be 0 or greater';
        }

        if (minutes < 0 || minutes > 59) {
            return 'Minutes must be between 0 and 59';
        }

        return '';
    }, []);

    // Only update display value when the prop actually changes (not while editing)
    useEffect(() => {
        // Only update if value changed and we're not actively editing
        if (value !== previousValueRef.current && !isEditingRef.current) {
            setDisplayValue(decimalToTimeString(value));
            setIsValid(true);
            onValidChange?.(true);
            previousValueRef.current = value;
        }
    }, [value, decimalToTimeString, onValidChange]);

    const handleFocus = () => {
        isEditingRef.current = true;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setDisplayValue(inputValue);

        // Clear input
        if (!inputValue || inputValue.trim() === '') {
            const wasValid = isValid;
            setIsValid(true);
            if (!wasValid) {
                onValidChange?.(true);
            }
            onChange?.(undefined);
            return;
        }

        // Validate
        const validationError = validateTimeString(inputValue);
        const valid = !validationError;
        const validityChanged = valid !== isValid;
        setIsValid(valid);

        // Only notify parent if validity actually changed
        if (validityChanged) {
            onValidChange?.(valid);
        }

        if (valid) {
            const decimalValue = timeStringToDecimal(inputValue);
            onChange?.(decimalValue);
        }
    };

    const handleBlur = () => {
        isEditingRef.current = false;

        if (!displayValue) return;

        // Auto-format bare number (e.g. "3" → "3:00")
        const trimmed = displayValue.trim();
        if (/^\d{1,5}$/.test(trimmed)) {
            const formatted = `${trimmed}:00`;
            setDisplayValue(formatted);
            setIsValid(true);
            onValidChange?.(true);
            const decimalValue = parseInt(trimmed, 10);
            onChange?.(decimalValue);
            return;
        }

        // Reformat to standard format if valid
        if (isValid) {
            const decimalValue = timeStringToDecimal(trimmed);
            if (decimalValue !== undefined) {
                setDisplayValue(decimalToTimeString(decimalValue));
            }
        }
    };

    return (
        <TextField
            {...textFieldProps}
            value={displayValue}
            onFocus={handleFocus}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!isValid || textFieldProps.error}
            placeholder="0:00"
            inputProps={{
                ...textFieldProps.inputProps,
                pattern: '[0-9]{1,5}:[0-9]{2}',
            }}
        />
    );
};

TimeInput.displayName = 'TimeInput';
export default TimeInput;
